import { defineStore } from 'pinia';
import { useDepartmentStore } from '@/stores/department';
import constants from '@/utils/constants';
import { cloneDeep, get, uniqBy } from 'lodash';
import { useInstitutionStore } from '@/stores/institution';
import { useProfilesStore } from '@/stores/profiles';
import { getUniqueInstitutions } from '@/utils/helpers';
import { IInstitution } from '@/interfaces';
import api from '@/api';
import { ActivitySubscription, ScreenProfile } from '@/graphql/generated-types';
import {
  ActivityFragment,
  SelectedActivityFragment,
} from '@/graphql/custom-types';
import { toAPIDate } from '@/utils/date-fns';
import { addMonths } from 'date-fns';

const DEFAULT_ACTIVITY: SelectedActivityFragment = {
  __typename: 'SingleActivity',
  id: '',
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  coverImage: {
    __typename: 'Picture',
    blurhash: '',
    description: '',
    id: '',
    source: '',
  },
  participants: [],
  cancelled: false,
  institution: {
    __typename: 'Institution',
    id: '',
    name: '',
    color: '',
    logo: { __typename: 'Picture', id: '', source: '' },
  },
  location: '',
  signupEnd: '',
  signupPossible: true,
  signupStart: '',
  pictures: [],
  departments: [],
  groups: [],
  videos: [],
  pictograms: [],
  documents: [],
  club: {
    __typename: 'Club',
    id: '',
    color: '',
    name: '',
    logo: { __typename: 'Picture', id: '', source: '' },
  },
  signupEnabled: false,
  maxSignups: 0,
  responsible: [],
  price: [{ __typename: 'EventPrice', price: 0, currency: 'DKK' }],
};

export interface IActivityState {
  all: ActivityFragment[];
  external: ActivityFragment[];
  selected: SelectedActivityFragment;
  uniqueInstitutions: IInstitution[] | null;
  allActivities: ActivityFragment[];
  allCopy: ActivityFragment[];
}

export const useActivitiesStore = defineStore('activities', {
  state: (): IActivityState => ({
    all: [],
    external: [],
    selected: DEFAULT_ACTIVITY,
    uniqueInstitutions: null,
    allActivities: [],
    allCopy: [],
  }),
  actions: {
    async getActivities() {
      const departmentStore = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      try {
        const allActivities = await api.activities.getActivityRange(
          departmentStore.id,
          toAPIDate(new Date()),
          toAPIDate(addMonths(new Date(), 6))
        );

        const institutionStore = useInstitutionStore();

        this.all = allActivities;

        const externalConnections = institutionStore.externalConnections || [];
        const hasExternalActivityModule = (
          departmentStore.settings?.screenConfig?.footerConfig ?? []
        ).some((item) => item === 'EXTERNAL_ACTIVITIES');

        if (externalConnections.length && hasExternalActivityModule) {
          const externalActivityInstitutionId = get(
            externalConnections,
            '[0.institution.id',
            '-1'
          );

          const externalActivities = allActivities.filter(
            (activity) =>
              activity.institution?.id === externalActivityInstitutionId
          );

          this.external = externalActivities;

          const internalActivities = allActivities.filter(
            (activity) =>
              activity.institution?.id !== externalActivityInstitutionId
          );

          this.all = internalActivities;
        }

        this.allCopy = cloneDeep(this.all);
        this.uniqueInstitutions = getUniqueInstitutions(this.all);
      } catch (error) {
        console.log('Error getting activities', error);
      }
    },
    async getSelectedActivity({ activityId }: { activityId: string }) {
      const activityEntry = await api.activities.getActivityEntry(activityId);

      this.selected = {
        ...activityEntry,
        participants: (activityEntry?.participants || []).map((participant) => {
          if (!participant.showImage)
            participant.displayImage = constants.avatarPlaceholder;
          return participant;
        }),
        pictures: uniqBy(
          (activityEntry.pictures || []).concat(
            activityEntry.coverImage ? [activityEntry.coverImage] : []
          ),
          'id'
        ),
      } as unknown as SelectedActivityFragment;
    },
    async activitySignupScreen(
      participating: boolean,
      subscription: ActivitySubscription,
      profile: ScreenProfile
    ) {
      const profilesStore = useProfilesStore();

      const mutation = participating
        ? api.activities.signup
        : api.activities.cancelSignup;

      await mutation(profile, subscription)
        .then(async () => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: true,
          });

          await this.getSelectedActivity({ activityId: subscription.id });
        })
        .catch((error) => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: false,
          });
          throw new Error('Error signing up to activity', error);
        });
    },
  },
});
